import { useControlledState } from '@react-stately/utils'
import type { Node } from '@react-types/shared'
import * as R from 'remeda'
import React, {
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { HiddenSelect, useSelect, Key } from 'react-aria'
import { useSelectState } from 'react-stately'
import { ThemeProviderClean } from '../../../../ThemeProviderClean'
import { Box, Flex, RootPortal, ScreenReaderOnly, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Events } from '../../../types'
import { useElementSize } from '../../hooks'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import { BaseSelectPopover } from './BaseSelectPopover'
import { BaseSelectValue } from './BaseSelectValue'
import { BaseSelectEndContent, StyledSelectTrigger } from './StyledSelectTrigger'
import { MIN_SELECT_LIST_WIDTH } from './constants'
import { createCascadePath } from './createCascadePath'
import { DynamicCascadeItem, DynamicItem, dynamicItemToItem } from './dynamicItemUtils'
import { ItemData } from './itemData.types'
import { useCascadeSelectState } from './useCascaseSelectState'
import { BaseSelectCascade } from './BaseSelectCascade'
import { CascadeListFocusProvider, useCascadeListFocus } from './CascadeListFocusContext'

type Variant = 'regular' | 'nested' | 'ghost'

export type BaseSelectProps = {
  INTERNAL_hideSelectList?: boolean
  INTERNAL_isOpen?: boolean
  'data-id'?: string
  'data-test-id'?: string
  defaultValue?: string
  errorMessage?: string
  eventsRef?: RefObject<Events<'open'> | null | undefined>
  hasLabelInside?: boolean
  hasUserProvidedSearch: boolean
  hasSearch?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  isLoading?: boolean
  label: React.ReactNode
  listStateData: {
    readonly existingValues: ReadonlySet<string>
    readonly itemData: Record<Key, ItemData>
    readonly options: ReadonlyArray<DynamicItem>
  }
  name?: string
  onChange?: (value: string, path: string[]) => void
  onSearchChange?: (value: string) => void
  placeholder?: React.ReactNode
  search?: string
  selectedCascadeItem?: DynamicCascadeItem[]
  selectedText?: (
    selectedItem: Node<DynamicItem>,
    valuePath: readonly string[] | undefined,
    value?: string
  ) => ReactElement
  setSelectedCascadeItem?: React.Dispatch<React.SetStateAction<DynamicCascadeItem[] | undefined>>
  startIcon?: React.ComponentType
  useVirtualScroll?: boolean
  value?: string
  valuePath?: readonly string[]
  variant?: Variant
  size?: 'regular' | 'large'
} & PositioningProps &
  FocusableHandlerProps

// eslint-disable-next-line complexity
const BaseSelectInternal = ({
  INTERNAL_hideSelectList,
  'data-id': dataId,
  'data-test-id': dataTestId,
  errorMessage,
  hasSearch,
  label,
  listStateData,
  hasLabelInside = false,
  name,
  placeholder = 'Select...',
  value,
  valuePath,
  variant = 'regular',
  isRequired,
  isDisabled,
  isLoading,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onSearchChange,
  search = '',
  startIcon,
  selectedText,
  eventsRef,
  INTERNAL_isOpen,
  useVirtualScroll,
  setSelectedCascadeItem,
  selectedCascadeItem,
  hasUserProvidedSearch,
  size = 'regular',
  ...positioningProps
}: BaseSelectProps) => {
  validatePositioningProps(positioningProps, 'BaseSelect')

  const { observeCollection, getCollection, queueFocus, ref: setListRef } = useCascadeListFocus()

  const rootValue = getRootValue(valuePath, value, defaultValue)
  const loadingNestedValuesState = useRef(R.doNothing())

  const {
    state,
    triggerRef,
    labelProps,
    descriptionProps,
    selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
    keyboardDelegate,
  } = useSelectProps({
    listStateData,
    isDisabled,
    label,
    errorMessage,
    value: selectedCascadeItem?.length ? selectedCascadeItem[0].value : rootValue,
    defaultValue,
    onChange: onChange
      ? value => onChange(value as string /* we allow only string values in items */, [])
      : undefined,
    onBlur,
    onFocus,
    INTERNAL_isOpen,
    onOpenChange: isOpen => {
      loadingNestedValuesState.current()
      if (!isOpen) {
        onSearchChange?.('')
        setSelectedCascadeItem?.(undefined)
        state.selectionManager.setFocused(false)
        state.selectionManager.setFocusedKey(null)
      } else {
        let stop = false
        loadingNestedValuesState.current = () => {
          stop = true
        }
        const recheckSelectedCascadeItems = () => {
          if (state.selectedItem?.value?.itemType === 'cascade' && valuePath) {
            const selectedCascadeItems = createCascadePath(
              valuePath,
              state.selectedItem.value,
              getCollection
            )
            setSelectedCascadeItem?.(selectedCascadeItems)
            if (selectedCascadeItems.length < valuePath.length && !stop) {
              observeCollection(
                valuePath.slice(0, selectedCascadeItems.length).join('.'),
                recheckSelectedCascadeItems
              )
            }
          }
        }
        recheckSelectedCascadeItems()
        queueFocus(valuePath?.join('.') ?? '', value)
      }
    },
    onSelectCascadeItem: item => {
      setSelectedCascadeItem?.([item])
      queueFocus(item.value)
      onSearchChange?.('')
    },
  })

  useEffect(
    () =>
      setListRef(
        {
          keyboardDelegate,
          selectionManager: state.selectionManager,
          collection: state.collection,
        },
        ''
      ),
    [keyboardDelegate, state.selectionManager, state.collection, setListRef]
  )

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        open: () => {
          setTimeout(() => stateRef.current.open(), 0)
        },
      }
      return () => (eventsRef.current = undefined)
    }
  }, [eventsRef])

  const fallbackName = useId()
  const nameWithFallback = name ?? fallbackName

  const { width: triggerWidth } = useElementSize(triggerRef)

  const rootValueNode = rootValue ? state.collection.getItem(rootValue) : undefined

  const [leafEndContent, setLeafEndContent] = useState<ReactNode | undefined>()
  // TODO HiddenSelect is based on the root list state, in case of cascading select it will hold only the root value instead of real selected value, we should somehow include either the real value all the whole state (value + valuePath)
  return (
    <BaseSelectContainer
      label={label}
      isRequired={isRequired}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      hasLabelInside={hasLabelInside}
      positioningProps={positioningProps}
    >
      <HiddenSelect state={state} triggerRef={triggerRef} label={label} name={nameWithFallback} />
      <Box flexGrow={1} minWidth={0}>
        <StyledSelectTrigger
          data-id={dataId}
          data-test-id={dataTestId}
          isOpen={state.isOpen}
          variant={variant}
          hasError={!!errorMessage}
          h={getHeight(hasLabelInside, variant)}
          w='100%'
          pr={5}
          pl={5}
          borderRadius={variant !== 'nested' ? 10 : undefined}
          ariaProps={{
            ...selectTriggerProps,
            elementRef: triggerRef,
          }}
        >
          <Flex alignItems='center' justifyContent='space-between'>
            <BaseSelectValue
              startIcon={startIcon}
              valueProps={valueProps}
              hasLabelInside={hasLabelInside}
              isRequired={isRequired}
              labelProps={labelProps}
              label={label}
              rootValueNode={rootValueNode}
              selectedText={selectedText}
              valuePath={valuePath}
              value={value}
              placeholder={isLoading ? 'Loading...' : placeholder}
              onLeafEndContent={setLeafEndContent}
              size={size}
            />

            <BaseSelectEndContent
              errorMessage={errorMessage}
              variant={variant}
              endContent={
                state.selectedItem?.value?.itemType === 'item'
                  ? state.selectedItem?.value?.endContent
                  : leafEndContent
              }
            />
          </Flex>
        </StyledSelectTrigger>
        <RootPortal>
          {state.isOpen && !INTERNAL_hideSelectList && (
            <ThemeProviderClean>
              <BaseSelectPopover
                triggerRef={triggerRef}
                state={state}
                triggerWidth={Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)}
              >
                <BaseSelectCascade
                  state={state}
                  selectedCascadeItem={selectedCascadeItem}
                  value={value}
                  listStateData={listStateData}
                  hasUserProvidedSearch={hasUserProvidedSearch}
                  hasSearch={hasSearch}
                  search={search}
                  triggerWidth={triggerWidth}
                  menuProps={menuProps}
                  keyboardDelegate={keyboardDelegate}
                  isLoading={isLoading}
                  useVirtualScroll={useVirtualScroll}
                  onChange={onChange}
                  onSearchChange={onSearchChange}
                  setSelectedCascadeItem={setSelectedCascadeItem}
                />
              </BaseSelectPopover>
            </ThemeProviderClean>
          )}
        </RootPortal>
        <BaseSelectError
          errorMessage={errorMessage}
          variant={variant}
          errorMessageProps={errorMessageProps}
        />
      </Box>
    </BaseSelectContainer>
  )
}

export const BaseSelect = ({
  value,
  valuePath,
  defaultValue,
  onChange,
  ...props
}: BaseSelectProps) => {
  const inputValue = useMemo(() => (value ? { value, valuePath } : undefined), [value, valuePath])
  const inputDefaultValue = useMemo(
    () => ({ value: defaultValue, valuePath }),
    [defaultValue, valuePath]
  )
  const inputOnChange = useMemo(
    () =>
      onChange
        ? (data: { value: string; valuePath: string[] }) => onChange(data.value, data.valuePath)
        : undefined,
    [onChange]
  )
  const [stateValue, stateChange] = useControlledState(
    (() => {
      if (inputValue) {
        return inputValue
      }
      if (inputValue === undefined && onChange === undefined) {
        return undefined
      }
      return null
    })(),
    inputDefaultValue,
    inputOnChange
  )
  const remappedStateChange = useCallback(
    (value: string, valuePath: string[]) => stateChange({ value, valuePath }),
    [stateChange]
  )
  return (
    <CascadeListFocusProvider>
      <BaseSelectInternal
        {...props}
        value={stateValue?.value}
        valuePath={stateValue?.valuePath}
        defaultValue={defaultValue}
        onChange={remappedStateChange}
      />
    </CascadeListFocusProvider>
  )
}

const getHeight = (hasLabelInside: boolean, variant: Variant) => {
  if (hasLabelInside) {
    return 11
  }
  if (variant === 'nested') {
    return 9
  }
  return 8
}

const BaseSelectContainer = (props: {
  children: React.ReactNode
  descriptionProps: React.HTMLAttributes<unknown>
  hasLabelInside?: boolean
  isRequired?: boolean
  label: React.ReactNode
  labelProps: React.HTMLAttributes<unknown>
  positioningProps: PositioningProps
}) => {
  if (props.hasLabelInside) {
    return <Box {...props.positioningProps}>{props.children}</Box>
  }
  return (
    <SettingLabelContainer
      isRequired={props.isRequired}
      label={props.label}
      labelElement='div'
      labelProps={props.labelProps}
      descriptionProps={props.descriptionProps}
      {...props.positioningProps}
    >
      {props.children}
    </SettingLabelContainer>
  )
}
const BaseSelectError = (props: {
  errorMessage?: string
  errorMessageProps: React.DOMAttributes<unknown>
  variant: Variant
}) => {
  if (!props.errorMessage) {
    return null
  }
  if (props.variant === 'nested') {
    return <ScreenReaderOnly {...props.errorMessageProps}>{props.errorMessage}</ScreenReaderOnly>
  }

  return (
    <Text
      mt={2}
      color='fg/caution'
      display='block'
      textStyle='body-multiline-tight'
      {...props.errorMessageProps}
    >
      {props.errorMessage}
    </Text>
  )
}

const useSelectProps = ({
  isDisabled,
  label,
  listStateData,
  errorMessage,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  INTERNAL_isOpen,
  onOpenChange,
  onSelectCascadeItem,
}: {
  INTERNAL_isOpen?: boolean
  defaultValue?: string
  errorMessage?: string
  isDisabled?: boolean
  label: React.ReactNode
  listStateData: {
    readonly disabledKeys?: readonly string[]
    readonly existingValues: ReadonlySet<string>
    readonly options: ReadonlyArray<DynamicItem>
  }
  onBlur?: (event: React.FocusEvent) => void
  onChange: Parameters<typeof useSelectState>[0]['onSelectionChange']
  onFocus?: (event: React.FocusEvent) => void
  onOpenChange?: (isOpen: boolean) => void
  onSelectCascadeItem: (item: DynamicCascadeItem) => void
  value?: string
}) => {
  const ariaProps = {
    isDisabled,
    label,
    errorMessage,
    disabledKeys: listStateData.disabledKeys,
    children: dynamicItemToItem,
    items: listStateData.options,
    selectedKey: (() => {
      if (value === undefined && onChange === undefined) {
        return undefined
      }
      if (listStateData.existingValues.has(value as string)) {
        return value
      }
      return null
    })(),
    defaultSelectedKey: listStateData.existingValues.has(defaultValue as string)
      ? defaultValue
      : undefined,
    onSelectionChange: onChange,
    onFocus,
    onBlur,
    isOpen: INTERNAL_isOpen,
    onOpenChange,
    isInvalid: Boolean(errorMessage),
  }
  const state = useCascadeSelectState(useSelectState(ariaProps), onSelectCascadeItem)

  useEffect(() => {
    /**
     * This is a hack to trigger the intersection observer for items around the active item, which
     * seems like a sensible number. This is used to load the next/previous pages of items in the
     * select when navigating through collapsed select using keyboard.
     */
    const itemsAroundActive: Array<Node<DynamicItem>> = []

    const activeItemIndex = (() => {
      let index = 0
      for (const key of state.collection.getKeys()) {
        if (key === state.selectedItem?.key) {
          return index
        }
        index++
      }
    })()

    // Get 5 items before and after active item
    for (const element of state.collection) {
      const elementIndex = state.collection.getItem(element.key)?.index ?? -1
      if (
        activeItemIndex !== undefined &&
        elementIndex >= activeItemIndex - 5 &&
        elementIndex <= activeItemIndex + 5
      ) {
        itemsAroundActive.push(element)
      }
    }

    itemsAroundActive.forEach(item => {
      ;(item?.rendered as any)?.props?.children?.props?.onIntersection?.()
    })
  }, [state])

  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const {
    labelProps,
    descriptionProps,
    triggerProps: selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
  } = useSelect({ ...ariaProps, keyboardDelegate }, state, triggerRef)
  return {
    keyboardDelegate,
    state,
    triggerRef,
    labelProps,
    descriptionProps,
    selectTriggerProps,
    valueProps,
    menuProps,
    errorMessageProps,
  }
}

const getRootValue = (
  valuePath: readonly string[] | undefined,
  value: string | undefined,
  defaultValue: string | undefined
) => {
  if (valuePath?.length) {
    return valuePath[0]
  }
  if (value === undefined) {
    return defaultValue
  }
  return value
}
